import { api } from "@Aspecto/backend/convex/_generated/api";
import { useUser } from "@clerk/tanstack-react-start";
import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { useMutation } from "convex/react";
import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { AlertCircle, Pencil } from "lucide-react";

import { AppPageContent } from "@/components/layout/app-page";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TimezoneSelect } from "@/components/ui/timezone-select";
import { extractConvexErrorMessage } from "@/lib/utils";
import { invalidateClientClerkAuthCache } from "@/routes/__root";

const COMMUNITY_URL_HOST = "afinio.co";

function normalizeSubdomain(raw: string) {
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "")
    .slice(0, 63);
}

export const Route = createFileRoute("/_authenticated/app/onboarding")({
  component: OnboardingPage,
});

function OnboardingPage() {
  const navigate = useNavigate();
  const router = useRouter();
  const { user: clerkUser } = useUser();
  const completeOnboarding = useMutation(api.users.completeOnboarding);

  const [orgName, setOrgName] = useState("");
  const [subdomainInput, setSubdomainInput] = useState("");
  const [userEditedSubdomain, setUserEditedSubdomain] = useState(false);
  const [isEditingSubdomain, setIsEditingSubdomain] = useState(false);
  const [timezone, setTimezone] = useState("UTC");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittedRef = useRef(false);

  const trimmedOrgName = orgName.trim();
  const canSuggest = trimmedOrgName.length >= 2;

  const { data: suggestion } = useQuery({
    ...convexQuery(
      api.subdomains.suggestSubdomain,
      canSuggest && !userEditedSubdomain ? { base: trimmedOrgName } : "skip",
    ),
  });

  const normalizedSubdomain = normalizeSubdomain(subdomainInput);

  const { data: availability } = useQuery({
    ...convexQuery(
      api.subdomains.isSubdomainAvailable,
      userEditedSubdomain && normalizedSubdomain.length >= 2
        ? { slug: normalizedSubdomain }
        : "skip",
    ),
  });

  useEffect(() => {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (browserTimezone) {
      setTimezone(browserTimezone);
    }
  }, []);

  useEffect(() => {
    if (!userEditedSubdomain && suggestion?.slug) {
      setSubdomainInput(suggestion.slug);
    }
  }, [suggestion?.slug, userEditedSubdomain]);

  useEffect(() => {
    if (trimmedOrgName.length < 2) {
      setSubdomainInput("");
      setUserEditedSubdomain(false);
      setIsEditingSubdomain(false);
    }
  }, [trimmedOrgName.length]);

  const slugOk =
    !userEditedSubdomain || (availability?.available === true && normalizedSubdomain.length >= 2);

  const showUnavailable =
    userEditedSubdomain && normalizedSubdomain.length >= 2 && availability?.available === false;

  const canSubmit = trimmedOrgName.length >= 2 && slugOk && !isSubmitting;

  const handleComplete = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || submittedRef.current) {
      toast.error("Please fill in all required fields");
      return;
    }
    submittedRef.current = true;
    setIsSubmitting(true);
    try {
      const subdomain = await completeOnboarding({
        firstName: clerkUser?.firstName ?? undefined,
        lastName: clerkUser?.lastName ?? undefined,
        companyName: trimmedOrgName,
        timezone,
        subdomain: userEditedSubdomain ? normalizedSubdomain : undefined,
      });
      toast.success("Organization setup complete!");
      invalidateClientClerkAuthCache();
      await router.invalidate();
      if (subdomain) {
        window.location.href = `https://${subdomain}.${COMMUNITY_URL_HOST}/app?setup=complete`;
      } else {
        navigate({ to: "/app" });
      }
    } catch (error) {
      submittedRef.current = false;
      toast.error(
        extractConvexErrorMessage(error) || "Failed to complete setup. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppPageContent className="flex min-h-svh flex-col justify-center">
      <Card className="mx-auto w-full max-w-lg shadow-lg">
        <CardHeader>
          <CardTitle className="text-2xl">Setup your organization</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={(e) => void handleComplete(e)} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="orgName">
                Organisation name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="orgName"
                placeholder="Acme Inc."
                value={orgName}
                onChange={(e) => {
                  setOrgName(e.target.value);
                  setUserEditedSubdomain(false);
                }}
                disabled={isSubmitting}
              />
            </div>

            {canSuggest && (
              <div className="space-y-1">
                {isEditingSubdomain ? (
                  <div className="flex items-center rounded-md border border-input bg-background text-sm shadow-sm focus-within:ring-2 focus-within:ring-ring">
                    <input
                      id="subdomain"
                      className="flex-1 bg-transparent px-3 py-2 outline-none"
                      value={subdomainInput}
                      onChange={(e) => {
                        setUserEditedSubdomain(true);
                        setSubdomainInput(e.target.value);
                      }}
                      onBlur={() => {
                        const normalized = normalizeSubdomain(subdomainInput);
                        setSubdomainInput(normalized);
                        if (normalized.length >= 2) setIsEditingSubdomain(false);
                      }}
                      autoFocus
                      autoCapitalize="none"
                      autoCorrect="off"
                      disabled={isSubmitting}
                    />
                    <span className="shrink-0 pr-3 text-muted-foreground">
                      .{COMMUNITY_URL_HOST}
                    </span>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditingSubdomain(true);
                      setUserEditedSubdomain(true);
                    }}
                    disabled={isSubmitting}
                    className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <span>Your community URL:</span>
                    <span className="font-medium text-foreground">
                      {subdomainInput || "…"}.{COMMUNITY_URL_HOST}
                    </span>
                    <Pencil className="h-3 w-3" />
                  </button>
                )}
                {showUnavailable && (
                  <div className="flex items-start gap-2.5 rounded-lg border border-destructive/40 bg-destructive/10 px-3.5 py-3 text-sm text-destructive">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    <div>
                      <p className="font-medium">This subdomain is not available</p>
                      <p className="mt-0.5 text-destructive/80">Please choose a different one.</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label>Time zone</Label>
              <TimezoneSelect value={timezone} onChange={setTimezone} disabled={isSubmitting} />
            </div>

            <Button type="submit" disabled={!canSubmit} className="w-full">
              {isSubmitting ? "Saving..." : "Complete setup"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </AppPageContent>
  );
}
